import { ArrowLeft, Crosshair, Plus } from "lucide-react";
import { type FormEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CargoEditorCameraState } from "../scenes/cargo-editor-scene";

export type CargoCameraView = {
  id: string;
  name: string;
  state: CargoEditorCameraState;
};

type CargoSceneBrowserProps = {
  activeViewId?: string;
  isNaming: boolean;
  isPositioning: boolean;
  onCancelNaming: () => void;
  onExit: () => void;
  onFinishPositioning: () => void;
  onSaveView: (name: string) => void;
  onSelectView: (view: CargoCameraView) => void;
  onStartPositioning: () => void;
  views: CargoCameraView[];
};

export function CargoSceneBrowser({
  activeViewId,
  isNaming,
  isPositioning,
  onCancelNaming,
  onExit,
  onFinishPositioning,
  onSaveView,
  onSelectView,
  onStartPositioning,
  views
}: CargoSceneBrowserProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const listRef = useRef<HTMLDivElement>(null);
  const [activeIndicator, setActiveIndicator] = useState<
    { offset: number; width: number } | undefined
  >();
  const [name, setName] = useState("");

  useLayoutEffect(() => {
    const list = listRef.current;
    const activeItem = activeViewId ? itemRefs.current.get(activeViewId) : undefined;
    if (!list || !activeItem) {
      setActiveIndicator(undefined);
      return;
    }

    const updateIndicator = () => {
      setActiveIndicator({
        offset: activeItem.offsetLeft,
        width: activeItem.offsetWidth
      });
    };
    updateIndicator();
    window.addEventListener("resize", updateIndicator);
    return () => window.removeEventListener("resize", updateIndicator);
  }, [activeViewId]);

  useEffect(() => {
    if (!isPositioning) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || event.repeat || event.isComposing) {
        return;
      }
      event.preventDefault();
      onFinishPositioning();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPositioning, onFinishPositioning]);

  useEffect(() => {
    if (!isNaming) {
      return;
    }
    setName("");
    const frameId = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frameId);
  }, [isNaming]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const nextName = name.trim();
    if (nextName) {
      onSaveView(nextName);
    }
  };

  return (
    <div className="cargo-browser-ui">
      {isPositioning ? (
        <div className="cargo-browser-position-hint" role="status">
          移动并旋转相机，按 Enter 保存当前视角
        </div>
      ) : null}
      <div className="cargo-browser-toolbar">
        <button
          aria-label="退出浏览"
          className="cargo-browser-exit"
          onClick={onExit}
          title="退出浏览"
          type="button"
        >
          <ArrowLeft aria-hidden="true" size={17} />
        </button>
        <button
          aria-label={isPositioning ? "正在定位，调整视角后按 Enter" : "添加视角"}
          className={`cargo-browser-add${isPositioning ? " is-positioning" : ""}`}
          disabled={isPositioning || isNaming}
          onClick={onStartPositioning}
          title={isPositioning ? "调整视角后按 Enter" : "添加视角"}
          type="button"
        >
          {isPositioning ? (
            <Crosshair aria-hidden="true" size={17} />
          ) : (
            <Plus aria-hidden="true" size={17} />
          )}
        </button>
      </div>

      {views.length > 0 ? (
        <aside className="cargo-browser-views" aria-label="已保存视角">
          <div className="cargo-browser-view-list" ref={listRef}>
            {activeIndicator ? (
              <span
                aria-hidden="true"
                className="cargo-browser-active-indicator"
                style={{
                  transform: `translateX(${activeIndicator.offset}px)`,
                  width: activeIndicator.width
                }}
              />
            ) : null}
            {views.map((view) => (
              <button
                aria-pressed={activeViewId === view.id}
                className={activeViewId === view.id ? "is-active" : undefined}
                disabled={isPositioning || isNaming}
                key={view.id}
                onClick={() => onSelectView(view)}
                ref={(element) => {
                  if (element) {
                    itemRefs.current.set(view.id, element);
                  } else {
                    itemRefs.current.delete(view.id);
                  }
                }}
                type="button"
              >
                <span>{view.name}</span>
              </button>
            ))}
          </div>
        </aside>
      ) : null}

      {isNaming ? (
        <div className="cargo-browser-dialog-backdrop">
          <form
            aria-labelledby="cargo-browser-dialog-title"
            aria-modal="true"
            className="cargo-browser-dialog"
            onSubmit={handleSubmit}
            role="dialog"
          >
            <div>
              <span>NEW CAMERA VIEW</span>
              <h2 id="cargo-browser-dialog-title">保存当前视角</h2>
            </div>
            <label>
              <span>视角名称</span>
              <input
                maxLength={80}
                onChange={(event) => setName(event.target.value)}
                ref={inputRef}
                value={name}
              />
            </label>
            <div className="cargo-browser-dialog-actions">
              <button onClick={onCancelNaming} type="button">
                取消
              </button>
              <button disabled={!name.trim()} type="submit">
                保存
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
